import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react'
import identity from 'lodash/identity'
import { configure } from 'mobx'
import { enableStaticRendering } from 'mobx-react-lite'
import type { AppProps } from 'next/app'
import { Inter } from 'next/font/google'
import NProgress from 'nprogress'
import React, { useEffect, useMemo } from 'react'
import { Provider } from 'urql'

import { Layout } from '@components/layout'
import { DefaultMeta } from '@contentful-components/page-meta/default-meta'
import { getOrCreateContentfulClient } from '@graphql/client'
import { useAnalytics } from '@lib/hooks/useAnalytics'
import { useCookieConsent } from '@lib/hooks/useCookieConsent'
import { useRouterProgress } from '@lib/hooks/useRouterProgress'
import { RootStore } from '@lib/store/RootStore'
import { RootStoreProvider } from '@lib/store/useStores'
import { UIContextProvider } from '@lib/store/useUIStore'
import type { PageWithContext } from '@lib/utils/context'
import { initSecurityMonitoring } from '@lib/utils/security-monitor'
import { isBrowser, isPreviewMode } from '@shared/util/env'
import 'normalize.css/normalize.css'
import 'nprogress/nprogress.css'
import '../styles/index.scss'

const inter = Inter({
    subsets: ['latin', 'latin-ext'],
    variable: '--font-inter',
    display: 'swap',
})

// TODO (@cataline): Remove this
configure({ enforceActions: 'never' })

NProgress.configure({ showSpinner: false })

// Suppress layoutEffect warnings for SSR
if (!isBrowser()) {
    React.useLayoutEffect = React.useEffect
    enableStaticRendering(true)
}

const App = ({ Component, pageProps, router }: Props) => {
    // Initialize RootStore client-side only
    const rootStore = useMemo(() => RootStore.fromState({}), [])

    // Initialize GraphQL client
    const graphQLClient = useMemo(
        () => getOrCreateContentfulClient(router.asPath),
        [router.asPath],
    )

    // App initialization hooks
    useRouterProgress()
    useAnalytics()
    useCookieConsent()

    // One-time initialization
    useEffect(() => {
        initSecurityMonitoring()
    }, [])

    // Load feature overrides from localStorage
    useEffect(() => {
        rootStore.load()
    }, [rootStore])

    const getLayout = Component.getLayout ? Component.getLayout : identity

    return (
        <div className={`${inter.variable} ${inter.className}`}>
            <ContentfulLivePreviewProvider
                locale='en-US'
                enableInspectorMode={isPreviewMode()}
                enableLiveUpdates={isPreviewMode()}
                targetOrigin='https://app.contentful.com'
            >
                <RootStoreProvider value={rootStore}>
                    {/* To make useQuery hook work, we need to pass graphQLClient to the graphqlProvider. */}
                    <Provider value={graphQLClient}>
                        <UIContextProvider>
                            <DefaultMeta />
                            <Layout
                                asPath={router.asPath}
                                announcement={pageProps?.data?.announcement}
                                disclaimers={
                                    pageProps?.data?.disclaimers ??
                                    pageProps?.article?.disclaimers
                                }
                            >
                                {getLayout(
                                    <Component {...pageProps} />,
                                    pageProps,
                                )}
                            </Layout>
                        </UIContextProvider>
                    </Provider>
                </RootStoreProvider>
            </ContentfulLivePreviewProvider>
        </div>
    )
}

type Props = AppProps & {
    Component: PageWithContext
}

export default App
